import React, { useState, useRef, useEffect } from 'react'
import styled, { css } from 'styled-components'
import { SPACING, TRANSITION_TIME_FAST_MS, TRANSITION_TIME_MD_MS } from '../styles/mixins/constants'
import { Carousel, CarouselItem } from './Carousel'
import { Image } from './Image'
import { ImageCanvas } from './ImageCanvas'
import { TakeoverContainer } from './TakeoverContainer'
import { Icon } from './Icon/index'
import { useDeviceSize } from '../styles/mixins/mediaQueryHooks'

type Data = {
    images: string[]
    path: string
    imagePaths: string[]
    id: string
    timezone?: string
}

type Props = {
    visibleData?: Data | null
    onClose?: () => void
}

const DESKTOP_ITEMS_PER_GROUP = 8
const LAPTOP_ITEMS_PER_GROUP = 6
const TABLET_ITEMS_PER_GROUP = 3
const MOBILE_ITEMS_PER_GROUP = 2

const RenderData = ({
    data,
    visibleData,
    visibleFilename,
    setVisibleFilename,
    visibleFilePath,
    setVisibleFilePath,
}: {
    data: Data
    visibleData: Data
    visibleFilename: string
    setVisibleFilename: React.Dispatch<React.SetStateAction<string>>
    visibleFilePath: string
    setVisibleFilePath: React.Dispatch<React.SetStateAction<string>>
}): JSX.Element => {
    const DEVICE_SIZE = useDeviceSize()

    let size: number
    switch (DEVICE_SIZE) {
        case 'desktop':
            size = DESKTOP_ITEMS_PER_GROUP
            break
        case 'laptop':
            size = LAPTOP_ITEMS_PER_GROUP
            break
        case 'tablet':
            size = TABLET_ITEMS_PER_GROUP
            break
        default:
            size = MOBILE_ITEMS_PER_GROUP
    }

    const { images, path, imagePaths, timezone } = data
    imagePaths.sort(function (a, b) {
        const aC = a
            .split('_')
            .filter((x) => x.length > 1)
            .join('')
        const bC = b
            .split('_')
            .filter((x) => x.length > 1)
            .join('')
        if (aC < bC) {
            return -1
        }
        if (aC > bC) {
            return 1
        }
        return 0
    })
    const groups = new Array(Math.ceil(imagePaths.length / size)).fill(null)
    const items: CarouselItem[] = groups.map((_, groupIdx) => {
        const itemIdx = groupIdx * size
        const groupImages = imagePaths.slice(itemIdx, itemIdx + size)
        const groupImagePaths = imagePaths.slice(itemIdx, itemIdx + size)

        if (groupImagePaths.length) {
            return {
                Slide: (
                    <ImageGroup key={`image_group_${itemIdx}`}>
                        {groupImagePaths.map((imagePath) => (
                            <ImageContainer
                                key={`image_${imagePath}`}
                                onClick={() => {
                                    setVisibleFilePath(imagePath)
                                }}
                            >
                                <Image
                                    filePath={imagePath}
                                    fileName={null}
                                    path={null}
                                    description="gate image"
                                />
                            </ImageContainer>
                        ))}
                    </ImageGroup>
                ),
                key: `group_${groupIdx}`,
            }
        }
        return {
            Slide: (
                <ImageGroup key={`image_group_${itemIdx}`}>
                    {groupImages.map((fileName) => (
                        <ImageContainer
                            key={`image_${fileName}`}
                            onClick={() => {
                                setVisibleFilename(fileName)
                            }}
                        >
                            <Image
                                filePath={null}
                                fileName={fileName}
                                path={path}
                                description="gate image"
                            />
                        </ImageContainer>
                    ))}
                </ImageGroup>
            ),
            key: `group_${groupIdx}`,
        }
    })
    return (
        <Content>
            <PrimaryImage>
                <ImageCanvas
                    path={visibleData?.path}
                    fileName={visibleFilename}
                    filePath={visibleFilePath}
                    description="gate image"
                    objectFit="contain"
                    disableBackground
                    timezone={timezone}
                />
            </PrimaryImage>
            <ImageSlider items={items} />
        </Content>
    )
}

export const ImageGallery = (props: Props): JSX.Element => {
    const { visibleData, onClose } = props
    const [visibleFilename, setVisibleFilename] = useState('')
    const [visibleFilePath, setVisibleFilePath] = useState('')

    useEffect(() => {
        if (visibleData) {
            setVisibleFilename(visibleData.images[0])
            setVisibleFilePath(visibleData.imagePaths[0])
        } else {
            onClose?.()
        }
    }, [visibleData, onClose])

    return (
        <TakeoverContainer isVisible={Boolean(visibleData)}>
            <>
                <CloseButton onClick={onClose}>
                    <Icon iconKey="Ex" color="inverted" size={8} />
                </CloseButton>
                {visibleData ? (
                    <RenderData
                        data={visibleData}
                        visibleData={visibleData}
                        visibleFilename={visibleFilename}
                        setVisibleFilename={setVisibleFilename}
                        visibleFilePath={visibleFilePath}
                        setVisibleFilePath={setVisibleFilePath}
                    />
                ) : (
                    <div />
                )}
            </>
        </TakeoverContainer>
    )
}

const ROW_HEIGHT_PX = 150

const Content = styled.div`
    display: grid;
    width: 100%;
    height: 100%;
    min-width: 300px;
    position: relative;
    grid-template-rows: 1fr ${ROW_HEIGHT_PX}px;
    grid-template-columns: 100%;
    grid-template-areas:
        'feature'
        'previews';
    grid-row-gap: ${SPACING[5]};
    padding: ${SPACING[5]} 0;
`

const PrimaryImage = styled.div`
    display: flex;
    flex: 1 1 auto;
    min-height: 0px;
    min-width: 0px;
    padding: 0 ${SPACING[5]};
    grid-area: feature;
`

const ImageSlider = styled(Carousel).attrs({ disableDots: true })`
    grid-area: previews;
`

const ImageContainer = styled.div`
    display: flex;
    flex: 0 0 auto;
    width: 150px;
    margin-right: ${SPACING[3]};
    cursor: pointer;
`

const ImageGroup = styled.div`
    flex: 0 0 auto;
    display: flex !important;
    flex-flow: row nowrap;
    height: 100%;
    width: 100%;
    justify-content: center;
    padding: 0 ${SPACING[5]};
`

const CloseButton = styled.div`
    position: absolute;
    z-index: 10;
    right: ${SPACING[2]};
    top: ${SPACING[2]};
    padding: ${SPACING[4]};
    border-radius: 100%;
    background-color: rgba(0, 0, 0, 0.75);
    opacity: 0.5;
    cursor: pointer;
    transition: opacity ${TRANSITION_TIME_FAST_MS}ms;
    &:hover {
        opacity: 1;
    }
    &:active {
        opacity: 0.5;
    }
`
